import type { User } from 'firebase/auth';

import { buildVerifyEmailPendingHref, requiresEmailVerification } from './emailVerificationGate';
import { enableWorkspaceSubstrateRoutes, getDefaultPostLoginPath } from './productCapabilities';

/** Production-safe default when `next` is missing or invalid (environment-only; no claims). */
export function getSafePostLoginFallback(): string {
  return getDefaultPostLoginPath();
}

/** Role ranks aligned with ProtectedRoute — higher satisfies lower required roles. */
const ROLE_RANK = {
  superadmin: 4,
  system_superadmin: 4,
  admin: 3,
  system_admin: 3,
  org_owner: 2,
  org_admin: 2,
  org_user: 1,
  individual: 1,
  premium: 1,
  user: 1,
} as const;

export function roleSatisfies(required: string, actual: string | null | undefined): boolean {
  if (!required) return true;
  if (!actual) return false;
  if (actual === 'superadmin') return true;
  const rq = ROLE_RANK[required as keyof typeof ROLE_RANK] ?? 0;
  const ac = ROLE_RANK[actual as keyof typeof ROLE_RANK] ?? 0;
  return ac >= rq;
}

/** True when Firebase custom claims satisfy workspace/org_user route guards. */
export function hasOrgUserClaimAccess(
  role: string | null | undefined,
  systemRole: string | null | undefined,
): boolean {
  return roleSatisfies('org_user', role) || roleSatisfies('org_user', systemRole);
}

const ORG_CLAIM_REQUIRED_EXACT = new Set(['/workspace', '/sandbox', '/u-value/materials']);

const ORG_CLAIM_REQUIRED_PREFIXES = [
  '/projects/',
  '/assessments/',
  '/workspace/',
  '/u-value/design-options/',
] as const;

/** Paths that ProtectedRoute serves only with org_user claims (or higher). */
export function pathRequiresOrgUserClaim(path: string): boolean {
  const { pathname } = splitPathnameAndSearch(path);
  if (pathname === '/u-value/calculator' || pathname === '/tools/u-value-calculator') {
    return false;
  }
  if (ORG_CLAIM_REQUIRED_EXACT.has(pathname)) return true;
  return ORG_CLAIM_REQUIRED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/**
 * Default post-login path when `next` is absent — claim-aware on staging/dev,
 * environment-only on production (workspace substrate disabled).
 */
export function getClaimAwareDefaultPostLoginPath(
  role: string | null | undefined,
  systemRole: string | null | undefined,
): string {
  if (!enableWorkspaceSubstrateRoutes()) {
    return '/u-value/calculator';
  }
  if (hasOrgUserClaimAccess(role, systemRole)) {
    return '/workspace';
  }
  return '/u-value/calculator';
}

export type PostLoginDestinationInput = {
  user: Pick<User, 'emailVerified'> | null | undefined;
  role: string | null | undefined;
  systemRole: string | null | undefined;
  rawNext: string | null | undefined;
};

/**
 * Resolves where to send a user immediately after sign-in (or auth session restore on /login).
 * Unverified users → verify-email. Verified users without org claims never land on workspace routes.
 */
export function resolvePostLoginDestination({
  user,
  role,
  systemRole,
  rawNext,
}: PostLoginDestinationInput): string {
  const fallback = getClaimAwareDefaultPostLoginPath(role, systemRole);

  if (user && requiresEmailVerification(user as User, systemRole, role)) {
    let pendingNext = sanitizeNextPath(rawNext, fallback);
    if (!hasOrgUserClaimAccess(role, systemRole) && pathRequiresOrgUserClaim(pendingNext)) {
      pendingNext = '/u-value/calculator';
    }
    return buildVerifyEmailPendingHref(pendingNext);
  }

  let destination = sanitizeNextPath(rawNext, fallback);
  if (!hasOrgUserClaimAccess(role, systemRole) && pathRequiresOrgUserClaim(destination)) {
    destination = '/u-value/calculator';
  }
  return destination;
}

const DENIED_EXACT_PATHS = new Set([
  '/login',
  '/auth/login',
  '/auth/register',
  '/auth/register-success',
  '/auth/verify-email',
  '/set-password',
  '/auth/reset',
]);

const ALLOWED_EXACT_PATHS = new Set([
  '/u-value/calculator',
  '/u-value/materials',
  '/u-value/saved-calculations',
  '/profile',
  '/settings',
  '/workspace',
  '/sandbox',
  '/tools/u-value-calculator',
]);

/** Pathname prefixes approved for post-auth return (e.g. dynamic ids). */
const ALLOWED_PATH_PREFIXES = ['/u-value/', '/projects/', '/assessments/', '/workspace/'] as const;

const HOSTILE_SCHEME_PATTERN = /[a-z][a-z0-9+.-]*:/i;

function splitPathnameAndSearch(path: string): { pathname: string; search: string } {
  const queryIndex = path.indexOf('?');
  if (queryIndex === -1) {
    return { pathname: path, search: '' };
  }
  return { pathname: path.slice(0, queryIndex), search: path.slice(queryIndex) };
}

function decodeForValidation(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function hasHostilePathShape(path: string): boolean {
  const trimmed = path.trim();
  if (!trimmed.startsWith('/')) return true;
  if (trimmed.startsWith('//')) return true;
  if (trimmed.includes('\\')) return true;
  if (HOSTILE_SCHEME_PATTERN.test(trimmed)) return true;
  return false;
}

function isAllowedPathname(pathname: string): boolean {
  if (!pathname.startsWith('/') || pathname.startsWith('//')) return false;
  if (DENIED_EXACT_PATHS.has(pathname)) return false;
  if (ALLOWED_EXACT_PATHS.has(pathname)) return true;
  return ALLOWED_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function isAllowedSearch(search: string): boolean {
  if (!search) return true;
  if (!search.startsWith('?')) return false;
  if (search.includes('\\')) return false;
  if (HOSTILE_SCHEME_PATTERN.test(search)) return false;
  if (search.includes('//')) return false;
  return true;
}

function isAllowedInternalReturnPath(path: string): boolean {
  if (hasHostilePathShape(path)) return false;
  const { pathname, search } = splitPathnameAndSearch(path);
  if (!isAllowedPathname(pathname)) return false;
  if (!isAllowedSearch(search)) return false;
  return true;
}

/**
 * Returns a safe in-app path for post-login navigation.
 * Rejects external URLs, protocol-relative paths, backslashes, and unknown routes.
 */
export function sanitizeNextPath(value: string | null | undefined, fallback: string): string {
  const safeFallback = isAllowedInternalReturnPath(fallback)
    ? fallback
    : '/u-value/calculator';

  if (value == null || value.trim() === '') {
    return safeFallback;
  }

  const candidates = [value.trim(), decodeForValidation(value.trim())];
  for (const candidate of candidates) {
    if (isAllowedInternalReturnPath(candidate)) {
      return candidate;
    }
  }

  return safeFallback;
}

/** Build `/login?next=…` with sanitised return path. */
export function buildLoginHrefWithNext(rawNext: string | null | undefined, fallback?: string): string {
  const safeNext = sanitizeNextPath(rawNext, fallback ?? getSafePostLoginFallback());
  return `/login?next=${encodeURIComponent(safeNext)}`;
}

/** Preserve the current location when redirecting anonymous users to login. */
export function buildProtectedLoginRedirect(pathname: string, search = ''): string {
  const returnPath = `${pathname}${search}`;
  return buildLoginHrefWithNext(returnPath);
}
